import * as React from 'react';

import { Button, TextField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useSetAtom } from 'jotai';

import { signUpMutationAtom } from '../hooks/mutations/signUpMutation';
import { $signUp } from '../state/signUp.state';

import { useTranslations } from '~/shared/i18n/useTranslations';

export const SignUpForm = () => {
  const t = useTranslations();
  const toast = useToast();

  const [signUpState, setSignUpState] = useAtom($signUp);

  // Form fields
  const [email, setEmail] = React.useState('');

  // Create mutation atom
  const $signUpMutation = React.useMemo(() => signUpMutationAtom(), []);

  const signUp = useSetAtom($signUpMutation);

  const [signUpMutationState] = useAtom($signUpMutation);

  const isPending = signUpMutationState.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Initiate signup and send verification email
      const signUpResult = await signUp({
        email,
      });

      if (
        signUpResult.authenticationSignUp.__typename === 'SignUpInitiatedResult'
      ) {
        // Show success message - verification email sent
        setSignUpState({
          ...signUpState,
          step: 'complete',
          email: signUpResult.authenticationSignUp.email,
          token: signUpResult.authenticationSignUp.token,
          completed: false,
        });
        toast.success(t.auth.signUp.verificationEmailSent);
        return;
      }

      if (
        signUpResult.authenticationSignUp.__typename ===
        'InvalidCredentialsError'
      ) {
        toast.error(t.auth.signUp.invalidCredentials);
        return;
      }

      toast.error(t.auth.signUp.unexpectedError);
    } catch (err) {
      toast.error(String(err));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="@SignUpForm flex flex-col gap-5">
      <div className="mb-2">
        <h1 className="text-2xl font-semibold text-foreground">
          {t.auth.signUp.title}
        </h1>
        <p className="mt-1 text-sm text-neutral">{t.auth.signUp.subtitle}</p>
      </div>

      <TextField
        autoFocus
        label={t.auth.signUp.emailLabel}
        placeholder="john@company.com"
        type="email"
        onChange={setEmail}
        value={email}
        isRequired
      />

      <Button
        type="submit"
        variant="solid"
        color="primary"
        isLoading={isPending}
      >
        {t.auth.signUp.continue}
      </Button>

      <div className="relative my-4 flex items-center gap-3">
        <div className="grow border-t border-neutral/20"></div>
        <span className="text-neutral">{t.auth.signUp.or}</span>
        <div className="grow border-t border-neutral/20"></div>
      </div>

      <div className="text-center text-sm text-neutral">
        {t.auth.signUp.alreadyHaveAccount}{' '}
        <a
          href="/sign-in"
          className="text-primary-darker hover:text-primary/80"
        >
          {t.auth.signUp.signIn}
        </a>
      </div>
    </form>
  );
};
